import React, { useState, useEffect } from 'react';
import { User } from '../types';
import Profile from './Profile';
import { GitPullRequest, Users, Settings as SettingsIcon, CheckSquare, BrainCircuit, LayoutTemplate, Database } from 'lucide-react';
import { ChecklistSettings } from './ChecklistSettings';
import { AiMatchingSettingsTab } from './AiMatchingSettingsTab';
import { StandardSectionsSettings } from './StandardSectionsSettings';
import BullhornIntegration from './BullhornIntegration';
import BullhornPipelineSync from './BullhornPipelineSync';
import { WordTemplateSettingsTab } from './WordTemplateSettingsTab';
import { EmailTemplatesSettingsTab } from './EmailTemplatesSettingsTab';
import { WhatsAppTemplatesSettingsTab } from './WhatsAppTemplatesSettingsTab';
import { CandidateSourcesSettingsTab } from './CandidateSourcesSettingsTab';
import { TalentPoolStatusesSettingsTab } from './TalentPoolStatusesSettingsTab';
import { PlacementTypesSettingsTab } from './PlacementTypesSettingsTab';
import { AiPromptsSettingsTab } from './AiPromptsSettingsTab';
import LogsManager from './LogsManager';
import SubscriptionManager from './SubscriptionManager';
import { FileText, Mail, Link as LinkIcon, MessageSquare, ShieldCheck, ClipboardList, Briefcase } from 'lucide-react';

interface SettingsProps {
    user: User;
    onLogout: () => void;
}

const Settings: React.FC<SettingsProps> = ({ user, onLogout }) => {
    const [activeTab, setActiveTab] = useState<'profile' | 'checklists' | 'sections' | 'ai_matching' | 'ai_prompts' | 'bullhorn_integration' | 'bullhorn_pipeline' | 'word_templates' | 'email_templates' | 'whatsapp_templates' | 'candidate_sources' | 'talentpool_statuses' | 'placement_types' | 'subscription' | 'logs'>('profile');

    useEffect(() => {
        const initial = localStorage.getItem('settings_initial_tab');
        if (initial) {
            setActiveTab(initial as any);
            localStorage.removeItem('settings_initial_tab');
        }
    }, []);

    const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';

    const hasTabAccess = (tabId: string) => {
        // "Mijn Profiel" is ALWAYS visible to everyone
        if (tabId === 'profile') return true;

        // Strictly lock down settings for Client Portal users (End-clients)
        if (isClientPortal) {
            return false;
        }

        // If they have explicit settings access defined in visibleTabs, respect that
        if (user.visibleTabs && user.visibleTabs.some(t => t.startsWith('settings_'))) {
            return user.visibleTabs.includes(`settings_${tabId}`);
        }
        
        // Legacy logic if they don't have explicit settings configs
        if (['bullhorn_integration', 'bullhorn_pipeline', 'word_templates', 'email_templates', 'whatsapp_templates', 'candidate_sources', 'talentpool_statuses', 'placement_types', 'subscription', 'logs'].includes(tabId)) {
            return user.role === 'CLIENT' || user.role === 'ADMIN' || user.role?.toUpperCase() === 'CLIENT';
        }
        
        // Everyone could see checklists, sections, ai_matching, ai_prompts by default previously
        return true;
    };

    return (
        <div className="flex flex-col lg:flex-row h-full bg-[#F8FAFC]">
            {/* Sidebar / Tabs */}
            <div className="w-full lg:w-64 bg-white border-r border-gray-100 p-6 flex flex-col gap-2">
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                    <SettingsIcon size={16} className="text-slate-400" /> Instellingen
                </h2>

                <button
                    onClick={() => setActiveTab('profile')}
                    className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'profile' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                >
                    <Users size={16} /> Mijn Profiel
                </button>

                {hasTabAccess('checklists') && (
                    <button
                        onClick={() => setActiveTab('checklists')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'checklists' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <CheckSquare size={16} /> Checklists
                    </button>
                )}

                {hasTabAccess('sections') && (
                    <button
                        onClick={() => setActiveTab('sections')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'sections' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <LayoutTemplate size={16} /> Standaard Secties
                    </button>
                )}

                {hasTabAccess('ai_matching') && (
                    <button
                        onClick={() => setActiveTab('ai_matching')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'ai_matching' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <BrainCircuit size={16} /> AI Matching
                    </button>
                )}

                {hasTabAccess('ai_prompts') && (
                    <button
                        onClick={() => setActiveTab('ai_prompts')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'ai_prompts' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <MessageSquare size={16} /> Prompt Instellingen
                    </button>
                )}

                {hasTabAccess('bullhorn_integration') && (
                    <button
                        onClick={() => setActiveTab('bullhorn_integration')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'bullhorn_integration' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <Database size={16} /> Bullhorn Integratie
                    </button>
                )}

                {hasTabAccess('bullhorn_pipeline') && (
                    <button
                        onClick={() => setActiveTab('bullhorn_pipeline')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'bullhorn_pipeline' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-indigo-50 hover:text-indigo-700'}`}
                    >
                        <GitPullRequest size={16} /> Pipeline Sync
                    </button>
                )}

                {hasTabAccess('word_templates') && (
                    <button
                        onClick={() => setActiveTab('word_templates')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'word_templates' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <FileText size={16} /> Word CV Templates
                    </button>
                )}

                {hasTabAccess('email_templates') && (
                    <button
                        onClick={() => setActiveTab('email_templates')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'email_templates' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <Mail size={16} /> Mail Templates
                    </button>
                )}

                {hasTabAccess('whatsapp_templates') && (
                    <button
                        onClick={() => setActiveTab('whatsapp_templates')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'whatsapp_templates' ? 'bg-[#25D366] text-white shadow-md' : 'text-slate-500 hover:bg-emerald-50 hover:text-emerald-700'}`}
                    >
                        <MessageSquare size={16} /> App Templates
                    </button>
                )}

                {hasTabAccess('candidate_sources') && (
                    <button
                        onClick={() => setActiveTab('candidate_sources')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'candidate_sources' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <LinkIcon size={16} /> Kandidaat Bronnen
                    </button>
                )}

                {hasTabAccess('talentpool_statuses') && (
                    <button
                        onClick={() => setActiveTab('talentpool_statuses')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'talentpool_statuses' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <Users size={16} /> Talentpool Statussen
                    </button>
                )}

                {hasTabAccess('placement_types') && (
                    <button
                        onClick={() => setActiveTab('placement_types')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'placement_types' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <Briefcase size={16} /> Plaatsing Types
                    </button>
                )}

                {hasTabAccess('subscription') && (
                    <button
                        onClick={() => setActiveTab('subscription')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'subscription' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <ShieldCheck size={16} /> Governance
                    </button>
                )}

                {hasTabAccess('logs') && (
                    <button
                        onClick={() => setActiveTab('logs')}
                        className={`flex items-center gap-3 p-3 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${activeTab === 'logs' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                        <ClipboardList size={16} /> Audit Logs
                    </button>
                )}
            </div>

            {/* Content Area */}
            <div className="flex-1 p-6 lg:p-10 overflow-y-auto">
                {activeTab === 'profile' && (
                    <Profile user={user} onLogout={onLogout} />
                )}
                {activeTab === 'checklists' && user.clientId && (
                    <ChecklistSettings clientId={user.clientId} />
                )}
                {activeTab === 'sections' && user.clientId && (
                    <StandardSectionsSettings clientId={user.clientId} />
                )}
                {activeTab === 'ai_matching' && user.clientId && (
                    <AiMatchingSettingsTab clientId={user.clientId} />
                )}
                {activeTab === 'ai_prompts' && user.clientId && (
                    <AiPromptsSettingsTab user={user} clientId={user.clientId} />
                )}
                {activeTab === 'bullhorn_integration' && user.clientId && (
                    <BullhornIntegration user={user} />
                )}
                {activeTab === 'bullhorn_pipeline' && user.clientId && (
                    <div className="-mx-6 -my-10 lg:-mx-10 h-full">
                        <BullhornPipelineSync user={user} />
                    </div>
                )}
                {activeTab === 'word_templates' && user.clientId && (
                    <WordTemplateSettingsTab clientId={user.clientId} />
                )}
                {activeTab === 'email_templates' && user.clientId && (
                    <EmailTemplatesSettingsTab clientId={user.clientId} userId={user.id} />
                )}
                {activeTab === 'whatsapp_templates' && user.clientId && (
                    <WhatsAppTemplatesSettingsTab clientId={user.clientId} />
                )}
                {activeTab === 'candidate_sources' && user.clientId && (
                    <CandidateSourcesSettingsTab clientId={user.clientId} />
                )}
                {activeTab === 'talentpool_statuses' && user.clientId && (
                    <TalentPoolStatusesSettingsTab clientId={user.clientId} />
                )}
                {activeTab === 'placement_types' && user.clientId && (
                    <PlacementTypesSettingsTab clientId={user.clientId} />
                )}
                {activeTab === 'subscription' && (
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                        <SubscriptionManager user={user} />
                    </div>
                )}
                {activeTab === 'logs' && (
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 h-full">
                        <LogsManager />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settings;
